/**
 * MESH Dashboard
 * BSI MESH Supply Chain Risk & Resilience Framework UI
 *
 * Tabs:
 *  1. Overview      — framework explainer
 *  2. Maturity      — interactive 16-question self-assessment
 *  3. Risk Scan     — AI-powered MESH-aligned risk analysis
 *  4. Supply Map    — multi-tier supplier visibility (Mapping pillar)
 */
import React, { useEffect, useState } from 'react';
import {
  Box, Tabs, Tab, Paper, Typography, Grid, Card, CardContent, Button,
  TextField, MenuItem, RadioGroup, FormControlLabel, Radio, Chip,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  CircularProgress, Alert, LinearProgress, Stack, Divider, Switch,
  Tooltip, Accordion, AccordionSummary, AccordionDetails, IconButton, List,
  ListItem, ListItemText, Dialog, DialogTitle, DialogContent, DialogActions,
} from '@mui/material';
import {
  AccountTree, Assessment, Map as MapIcon, Science, Warning,
  AutoAwesome, ExpandMore, Build, Description, CloudUpload, Delete, Search,
  Visibility, PlayArrow, Schedule, Add, Edit, Refresh, ChatBubble,
} from '@mui/icons-material';
import {
  meshApi, MaturityAnswers, MaturityResult, RiskScanResult,
  SupplyChainMap, GeoConcentrationResult, AnswerValue,
  StructuredRiskScanResult, StructuredMaturityResult,
  AgentAskResult, AgentToolCall, AgentTurn, Citation,
} from '../../services/meshApi';
import { docsApi, Document as RAGDoc, SearchResult as RAGSearchResult, DocsStats, ChunkDetail } from '../../services/docsApi';
import { conversationsApi, ConversationSummary, ConversationDetail } from '../../services/conversationsApi';
import { watchtowerApi, BriefingSummary, Briefing, SchedulerStatus } from '../../services/watchtowerApi';

const ANSWERS: AnswerValue[] = ['Yes', 'Partially', 'No'];

const QUESTIONS = {
  mapping: [
    'Do you have a complete map of tier-1 suppliers?',
    'Do you have visibility beyond tier-1 to tier-2/3 suppliers?',
    'Are supplier geographic concentrations documented?',
    'Do you track single-source dependencies?',
  ],
  evaluation: [
    'Do you score suppliers on geopolitical risk?',
    'Do you conduct ESG/human rights assessments?',
    'Do you monitor supplier financial health?',
    'Do you have formal cyber risk assessment for suppliers?',
  ],
  strategy: [
    'Do you have documented mitigation playbooks per risk category?',
    'Is dual/multi-sourcing applied to critical components?',
    'Do you have tested business continuity plans?',
    'Are response times defined for risk escalations?',
  ],
  harmonization: [
    'Is supply chain risk data shared across procurement, finance, and compliance?',
    'Are KPIs for resilience tracked at board level?',
    'Do supplier contracts include risk and compliance clauses?',
    'Is there a named owner for supply chain resilience?',
  ],
};

const PILLAR_COLORS: Record<string, 'success' | 'warning' | 'error' | 'info'> = {
  Optimised: 'success', Managed: 'success', Defined: 'info',
  Aware: 'warning', Reactive: 'error',
};

const RISK_CATEGORIES = [
  { value: 'geopolitical', label: 'Geopolitical Risk' },
  { value: 'esg_human_rights', label: 'ESG & Human Rights' },
  { value: 'financial', label: 'Financial Risk' },
  { value: 'operational', label: 'Operational Risk' },
  { value: 'cyber', label: 'Cyber & Technology' },
];

const RISK_LEVEL_COLOR: Record<string, 'success' | 'warning' | 'error' | 'info' | 'default'> = {
  Low: 'success', Medium: 'info', High: 'warning', Critical: 'error',
};

const PRIORITY_COLOR: Record<string, 'success' | 'warning' | 'error' | 'default'> = {
  Low: 'success', Medium: 'warning', High: 'error',
};

const fmtGBP = (n?: number) =>
  typeof n === 'number'
    ? new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', maximumFractionDigits: 0 }).format(n)
    : '—';

// ============================================================
// Structured AI Result Panels
// ============================================================
const StructuredRiskScanPanel: React.FC<{ data: StructuredRiskScanResult }> = ({ data }) => {
  const a = data.structured_analysis;
  return (
    <Card>
      <CardContent>
        <Stack direction="row" spacing={1} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
          <Chip label={`MESH Pillar: ${a.mesh_pillar}`} color="primary" />
          <Chip label={a.risk_category} color="secondary" />
          <Chip label={`Risk: ${a.overall_risk_level}`}
            color={RISK_LEVEL_COLOR[a.overall_risk_level] || 'default'} />
          <Chip label={`Confidence: ${a.confidence}`} variant="outlined" />
          <Chip label={a.time_horizon} variant="outlined" />
        </Stack>

        <Typography variant="subtitle2">Summary</Typography>
        <Typography variant="body2" sx={{ mb: 2 }}>{a.summary}</Typography>

        <Typography variant="subtitle2">Primary Drivers</Typography>
        <ul style={{ marginTop: 4, marginBottom: 12, paddingLeft: 20 }}>
          {a.primary_drivers.map((d, i) => <li key={i}><Typography variant="body2">{d}</Typography></li>)}
        </ul>

        <Divider sx={{ my: 2 }} />
        <Typography variant="subtitle2" gutterBottom>Risk Factors</Typography>
        <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Factor</TableCell>
                <TableCell align="center">Sev</TableCell>
                <TableCell align="center">Likelihood</TableCell>
                <TableCell align="center">Score</TableCell>
                <TableCell>MESH Indicator</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {a.risk_factors.map((rf, i) => (
                <TableRow key={i}>
                  <TableCell>{rf.factor}</TableCell>
                  <TableCell align="center">{rf.severity}</TableCell>
                  <TableCell align="center">{rf.likelihood}</TableCell>
                  <TableCell align="center">
                    <Chip size="small" label={rf.risk_score}
                      color={rf.risk_score >= 15 ? 'error' : rf.risk_score >= 9 ? 'warning' : 'success'} />
                  </TableCell>
                  <TableCell><Typography variant="caption">{rf.mesh_indicator}</Typography></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <Typography variant="subtitle2" gutterBottom>Strategy Recommendations</Typography>
        <Stack spacing={1} sx={{ mb: 2 }}>
          {a.strategy_recommendations.map((s, i) => (
            <Paper key={i} variant="outlined" sx={{ p: 1.5 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="body2" fontWeight={600}>{s.action}</Typography>
                <Chip size="small" label={s.priority} color={PRIORITY_COLOR[s.priority] || 'default'} />
              </Stack>
              <Typography variant="caption" color="text.secondary">
                {s.owner_role} • {s.timeline} • Cost {fmtGBP(s.estimated_cost_gbp)} • Impact {fmtGBP(s.estimated_impact_gbp)}
              </Typography>
            </Paper>
          ))}
        </Stack>

        <Typography variant="subtitle2" gutterBottom>Monitoring Indicators</Typography>
        <Stack spacing={1} sx={{ mb: 2 }}>
          {a.monitoring_indicators.map((m, i) => (
            <Alert key={i} severity="info" icon={<Assessment />}>
              <b>{m.indicator}</b> — {m.frequency} • owner: {m.owner}
              <br /><small>Alert: {m.alert_threshold}</small>
            </Alert>
          ))}
        </Stack>

        <Divider sx={{ my: 2 }} />
        <Typography variant="subtitle2">Escalation Trigger</Typography>
        <Typography variant="body2" sx={{ mb: 2 }}>{a.escalation_trigger}</Typography>

        <Typography variant="subtitle2">Functions to Involve</Typography>
        <Stack direction="row" flexWrap="wrap" gap={0.5} sx={{ mb: 2 }}>
          {a.functions_to_involve.map(f => <Chip key={f} label={f} size="small" />)}
        </Stack>

        <Typography variant="subtitle2">Assumptions</Typography>
        <ul style={{ marginTop: 4, paddingLeft: 20 }}>
          {a.assumptions.map((x, i) => <li key={i}><Typography variant="caption">{x}</Typography></li>)}
        </ul>
      </CardContent>
    </Card>
  );
};

const StructuredMaturityPanel: React.FC<{ data: StructuredMaturityResult }> = ({ data }) => {
  const i = data.structured_interpretation;
  return (
    <Grid item xs={12} md={6}>
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>Results — {data.company}</Typography>
          <Box sx={{ mb: 2 }}>
            <Typography variant="caption">OVERALL MATURITY</Typography>
            <Stack direction="row" alignItems="center" spacing={2}>
              <Typography variant="h3">{i.overall_score}</Typography>
              <Chip label={i.overall_level} color={PILLAR_COLORS[i.overall_level] || 'default'} />
            </Stack>
            <LinearProgress variant="determinate" value={(i.overall_score / 5) * 100}
              sx={{ height: 10, borderRadius: 5, mt: 1 }} />
          </Box>

          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="subtitle2">Headline Finding</Typography>
            <Typography variant="body2">{i.headline_finding}</Typography>
          </Alert>

          <Alert severity="error" sx={{ mb: 2 }}>
            <Typography variant="subtitle2">Biggest Competitive Risk</Typography>
            <Typography variant="body2">{i.biggest_competitive_risk}</Typography>
          </Alert>

          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle2" gutterBottom>Dimension Insights</Typography>
          {i.dimension_insights.map((d, idx) => (
            <Paper key={idx} variant="outlined" sx={{ p: 1.5, mb: 1 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="body2" fontWeight={600}>{d.dimension}</Typography>
                <Stack direction="row" spacing={1}>
                  <Typography variant="body2">{d.score}/5</Typography>
                  <Chip size="small" label={d.maturity_level}
                    color={PILLAR_COLORS[d.maturity_level] || 'default'} />
                </Stack>
              </Stack>
              <Typography variant="caption" color="error">Gap: {d.key_gap}</Typography><br />
              <Typography variant="caption" color="success.main">Strength: {d.key_strength}</Typography><br />
              <Typography variant="caption" color="text.secondary">{d.industry_benchmark}</Typography>
            </Paper>
          ))}

          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle2" gutterBottom>12-Month Roadmap</Typography>
          <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Q</TableCell>
                  <TableCell>Focus</TableCell>
                  <TableCell>Deliverable</TableCell>
                  <TableCell>Dim</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {i.twelve_month_roadmap.map((r, idx) => (
                  <TableRow key={idx}>
                    <TableCell><Chip size="small" label={r.quarter} /></TableCell>
                    <TableCell>{r.focus}</TableCell>
                    <TableCell><Typography variant="caption">{r.deliverable}</Typography></TableCell>
                    <TableCell><Chip size="small" variant="outlined" label={r.target_dimension} /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <Typography variant="subtitle2" gutterBottom>Quick Wins</Typography>
          {i.quick_wins.map((q, idx) => (
            <Alert key={idx} severity="success" sx={{ mb: 1 }}>{q}</Alert>
          ))}
        </CardContent>
      </Card>
    </Grid>
  );
};

const MESHDashboard: React.FC = () => {
  const [tab, setTab] = useState(0);

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
        <AccountTree color="primary" sx={{ fontSize: 40 }} />
        <Box>
          <Typography variant="h4" fontWeight={500}>MESH Resilience Framework</Typography>
          <Typography variant="body2" color="text.secondary">
            BSI MESH — Mapping, Evaluation, Strategy, Harmonization
          </Typography>
        </Box>
      </Stack>

      <Paper sx={{ mb: 3 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable">
          <Tab icon={<AccountTree />} iconPosition="start" label="Overview" />
          <Tab icon={<Assessment />} iconPosition="start" label="Maturity Assessment" />
          <Tab icon={<Science />} iconPosition="start" label="AI Risk Scan" />
          <Tab icon={<MapIcon />} iconPosition="start" label="Supply Chain Map" />
          <Tab icon={<AutoAwesome />} iconPosition="start" label="Ask the Agent" />
          <Tab icon={<Description />} iconPosition="start" label="Documents" />
          <Tab icon={<Visibility />} iconPosition="start" label="Watchtower" />
        </Tabs>
      </Paper>

      {tab === 0 && <OverviewTab />}
      {tab === 1 && <MaturityTab />}
      {tab === 2 && <RiskScanTab />}
      {tab === 3 && <SupplyMapTab />}
      {tab === 4 && <AgentTab />}
      {tab === 5 && <DocumentsTab />}
      {tab === 6 && <WatchtowerTab />}
    </Box>
  );
};

// ============================================================
// Overview Tab
// ============================================================
const OverviewTab: React.FC = () => (
  <Grid container spacing={3}>
    {[
      { letter: 'M', name: 'Mapping', color: '#1976d2',
        desc: 'Visibility of tier 1, 2, and 3 suppliers and their interdependencies' },
      { letter: 'E', name: 'Evaluation', color: '#388e3c',
        desc: 'Systematic assessment across 5 risk categories: geopolitical, ESG, financial, operational, cyber' },
      { letter: 'S', name: 'Strategy', color: '#f57c00',
        desc: 'Documented playbooks, dual-sourcing, and business continuity plans' },
      { letter: 'H', name: 'Harmonization', color: '#7b1fa2',
        desc: 'Cross-functional alignment of procurement, finance, compliance, and operations' },
    ].map(p => (
      <Grid item xs={12} sm={6} md={3} key={p.name}>
        <Card sx={{ height: '100%', borderTop: `4px solid ${p.color}` }}>
          <CardContent>
            <Typography variant="h2" sx={{ color: p.color, fontWeight: 700 }}>{p.letter}</Typography>
            <Typography variant="h6" gutterBottom>{p.name}</Typography>
            <Typography variant="body2" color="text.secondary">{p.desc}</Typography>
          </CardContent>
        </Card>
      </Grid>
    ))}
    <Grid item xs={12}>
      <Alert severity="info">
        Use the <b>Maturity Assessment</b> to score your current resilience posture, the
        <b> AI Risk Scan</b> to deep-dive a specific scenario, and the
        <b> Supply Chain Map</b> to visualise multi-tier supplier dependencies.
      </Alert>
    </Grid>
  </Grid>
);

// ============================================================
// Maturity Assessment Tab
// ============================================================
const MaturityTab: React.FC = () => {
  const [companyName, setCompanyName] = useState('Your Company');
  const [industry, setIndustry] = useState('Manufacturing');
  const [answers, setAnswers] = useState<MaturityAnswers>({
    mapping: ['No', 'No', 'No', 'No'],
    evaluation: ['No', 'No', 'No', 'No'],
    strategy: ['No', 'No', 'No', 'No'],
    harmonization: ['No', 'No', 'No', 'No'],
  });
  const [result, setResult] = useState<MaturityResult | null>(null);
  const [structured, setStructured] = useState<StructuredMaturityResult | null>(null);
  const [useStructured, setUseStructured] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const setAnswer = (dim: keyof MaturityAnswers, idx: number, value: AnswerValue) => {
    const next = [...answers[dim]] as AnswerValue[];
    next[idx] = value;
    setAnswers({ ...answers, [dim]: next });
  };

  const submit = async () => {
    setLoading(true);
    setError(null);
    setResult(null);
    setStructured(null);
    const req = {
      company_name: companyName,
      industry,
      annual_revenue: '£50M-100M',
      risk_tolerance: 'moderate',
      answers,
    };
    try {
      if (useStructured) {
        const res = await meshApi.runMaturityAssessmentStructured(req);
        setStructured(res);
      } else {
        const res = await meshApi.runMaturityAssessment(req);
        setResult(res);
      }
    } catch (e: any) {
      setError(e?.response?.data?.detail || e.message || 'Assessment failed');
    } finally {
      setLoading(false);
    }
  };

  const hasOutput = result || structured;
  return (
    <Grid container spacing={3}>
      <Grid item xs={12} md={hasOutput ? 6 : 12}>
        <Card>
          <CardContent>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
              <Typography variant="h6">Self-Assessment (16 questions)</Typography>
              <Tooltip title="Use Gemini structured JSON output (typed cards) instead of free-text markdown">
                <FormControlLabel
                  control={<Switch checked={useStructured} onChange={e => setUseStructured(e.target.checked)} />}
                  label="Structured AI"
                />
              </Tooltip>
            </Stack>
            <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
              <TextField label="Company" size="small" value={companyName}
                onChange={e => setCompanyName(e.target.value)} fullWidth />
              <TextField label="Industry" size="small" value={industry}
                onChange={e => setIndustry(e.target.value)} fullWidth />
            </Stack>
            {(Object.keys(QUESTIONS) as Array<keyof typeof QUESTIONS>).map(dim => (
              <Box key={dim} sx={{ mb: 3 }}>
                <Typography variant="subtitle1" sx={{ textTransform: 'capitalize', fontWeight: 600 }}>
                  {dim}
                </Typography>
                <Divider sx={{ mb: 1 }} />
                {QUESTIONS[dim].map((q, idx) => (
                  <Box key={idx} sx={{ mb: 1 }}>
                    <Typography variant="body2">{q}</Typography>
                    <RadioGroup row value={answers[dim][idx]}
                      onChange={e => setAnswer(dim, idx, e.target.value as AnswerValue)}>
                      {ANSWERS.map(a => (
                        <FormControlLabel key={a} value={a} control={<Radio size="small" />} label={a} />
                      ))}
                    </RadioGroup>
                  </Box>
                ))}
              </Box>
            ))}
            <Button variant="contained" onClick={submit} disabled={loading} fullWidth>
              {loading ? <CircularProgress size={22} /> : 'Run MESH Maturity Assessment'}
            </Button>
            {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
          </CardContent>
        </Card>
      </Grid>

      {structured && <StructuredMaturityPanel data={structured} />}
      {result && (
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Results — {result.company}</Typography>
              <Box sx={{ mb: 2 }}>
                <Typography variant="caption">OVERALL MATURITY</Typography>
                <Stack direction="row" alignItems="center" spacing={2}>
                  <Typography variant="h3">{result.overall.score}</Typography>
                  <Chip label={result.overall.maturity_level}
                    color={PILLAR_COLORS[result.overall.maturity_level] || 'default'} />
                </Stack>
                <LinearProgress variant="determinate"
                  value={(result.overall.score / 5) * 100}
                  sx={{ height: 10, borderRadius: 5, mt: 1 }} />
                <Typography variant="caption" color="text.secondary">
                  {result.overall.description}
                </Typography>
              </Box>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle2" gutterBottom>Per-Dimension Scores</Typography>
              {Object.entries(result.dimensions).map(([dim, d]) => (
                <Box key={dim} sx={{ mb: 1.5 }}>
                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                      {d.label}
                    </Typography>
                    <Stack direction="row" spacing={1}>
                      <Typography variant="body2" fontWeight={600}>{d.score}/5</Typography>
                      <Chip size="small" label={d.maturity_level}
                        color={PILLAR_COLORS[d.maturity_level] || 'default'} />
                    </Stack>
                  </Stack>
                  <LinearProgress variant="determinate" value={(d.score / 5) * 100}
                    sx={{ height: 6, borderRadius: 3 }} />
                </Box>
              ))}

              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle2" gutterBottom>Top Priority Actions</Typography>
              {result.priority_actions.map((a, i) => (
                <Alert key={i} severity="warning" sx={{ mb: 1 }}>
                  <b>{a.dimension}</b>: {a.focus_area}<br />
                  <small>{a.business_impact}</small>
                </Alert>
              ))}

              {result.ai_interpretation && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle2" gutterBottom>AI Interpretation</Typography>
                  <Paper sx={{ p: 2, bgcolor: 'grey.50', maxHeight: 400, overflow: 'auto' }}>
                    <pre style={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit', margin: 0, fontSize: 13 }}>
                      {result.ai_interpretation}
                    </pre>
                  </Paper>
                </>
              )}
            </CardContent>
          </Card>
        </Grid>
      )}
    </Grid>
  );
};

// ============================================================
// Risk Scan Tab
// ============================================================
const RiskScanTab: React.FC = () => {
  const [category, setCategory] = useState<RiskScanResult['risk_category'] | string>('geopolitical');
  const [scenario, setScenario] = useState(
    'New US tariffs on Chinese electronics components effective Q3 2026'
  );
  const [companyName, setCompanyName] = useState('Your Company');
  const [industry, setIndustry] = useState('Manufacturing');
  const [result, setResult] = useState<RiskScanResult | null>(null);
  const [structured, setStructured] = useState<StructuredRiskScanResult | null>(null);
  const [useStructured, setUseStructured] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setLoading(true); setError(null);
    setResult(null); setStructured(null);
    const req = {
      company_name: companyName,
      industry,
      risk_category: category as any,
      scenario,
    };
    try {
      if (useStructured) {
        const res = await meshApi.runRiskScanStructured(req);
        setStructured(res);
      } else {
        const res = await meshApi.runRiskScan(req);
        setResult(res);
      }
    } catch (e: any) {
      setError(e?.response?.data?.detail || e.message || 'Scan failed');
    } finally { setLoading(false); }
  };

  return (
    <Grid container spacing={3}>
      <Grid item xs={12} md={5}>
        <Card>
          <CardContent>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
              <Typography variant="h6">Configure Risk Scan</Typography>
              <Tooltip title="Use Gemini structured JSON output">
                <FormControlLabel
                  control={<Switch checked={useStructured} onChange={e => setUseStructured(e.target.checked)} />}
                  label="Structured AI"
                />
              </Tooltip>
            </Stack>
            <Stack spacing={2}>
              <TextField label="Company" value={companyName}
                onChange={e => setCompanyName(e.target.value)} fullWidth size="small" />
              <TextField label="Industry" value={industry}
                onChange={e => setIndustry(e.target.value)} fullWidth size="small" />
              <TextField select label="MESH Risk Category" value={category}
                onChange={e => setCategory(e.target.value)} fullWidth size="small">
                {RISK_CATEGORIES.map(c =>
                  <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>)}
              </TextField>
              <TextField label="Scenario" multiline rows={4} value={scenario}
                onChange={e => setScenario(e.target.value)} fullWidth />
              <Button variant="contained" onClick={submit} disabled={loading || !scenario}>
                {loading ? <CircularProgress size={22} /> : 'Run AI Risk Scan'}
              </Button>
              {error && <Alert severity="error">{error}</Alert>}
            </Stack>
          </CardContent>
        </Card>
      </Grid>
      <Grid item xs={12} md={7}>
        {structured ? (
          <StructuredRiskScanPanel data={structured} />
        ) : result ? (
          <Card>
            <CardContent>
              <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                <Chip label={`MESH Pillar: ${result.mesh_pillar}`} color="primary" />
                <Chip label={result.risk_category_label} color="secondary" />
              </Stack>
              <Typography variant="caption" color="text.secondary">SCENARIO</Typography>
              <Typography variant="body2" sx={{ mb: 2 }}>{result.scenario}</Typography>
              <Typography variant="subtitle2" gutterBottom>Key Indicators</Typography>
              <Stack direction="row" flexWrap="wrap" gap={0.5} sx={{ mb: 2 }}>
                {result.key_indicators.map(i => <Chip key={i} label={i} size="small" />)}
              </Stack>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle2" gutterBottom>AI Analysis</Typography>
              <Paper sx={{ p: 2, bgcolor: 'grey.50', maxHeight: 600, overflow: 'auto' }}>
                <pre style={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit', margin: 0, fontSize: 13 }}>
                  {result.analysis}
                </pre>
              </Paper>
            </CardContent>
          </Card>
        ) : (
          <Alert severity="info">Configure a scenario and run the scan to see a MESH-aligned AI risk analysis.</Alert>
        )}
      </Grid>
    </Grid>
  );
};

// ============================================================
// Supply Chain Map Tab
// ============================================================
const SupplyMapTab: React.FC = () => {
  const [map, setMap] = useState<SupplyChainMap | null>(null);
  const [geo, setGeo] = useState<GeoConcentrationResult | null>(null);
  const [singleSource, setSingleSource] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    setLoading(true); setError(null);
    try {
      const [m, g, s] = await Promise.all([
        meshApi.getMap(),
        meshApi.getGeoConcentration(),
        meshApi.getSingleSourceRisks(),
      ]);
      setMap(m); setGeo(g); setSingleSource(s);
    } catch (e: any) {
      setError(e.message || 'Failed to load map');
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  if (loading) return <CircularProgress />;
  if (error) return <Alert severity="error">{error}</Alert>;
  if (!map) return null;

  const tierColor = (t: number) =>
    t === 1 ? '#1976d2' : t === 2 ? '#f57c00' : t === 3 ? '#7b1fa2' : '#616161';

  return (
    <Grid container spacing={3}>
      {/* Summary cards */}
      <Grid item xs={12} sm={3}>
        <Card><CardContent>
          <Typography variant="caption" color="text.secondary">TOTAL NODES</Typography>
          <Typography variant="h3">{map.total_nodes}</Typography>
          <Typography variant="caption">across {map.tiers_mapped.length} tiers</Typography>
        </CardContent></Card>
      </Grid>
      <Grid item xs={12} sm={3}>
        <Card><CardContent>
          <Typography variant="caption" color="text.secondary">DEPENDENCIES</Typography>
          <Typography variant="h3">{map.total_edges}</Typography>
          <Typography variant="caption">supplier-to-supplier links</Typography>
        </CardContent></Card>
      </Grid>
      <Grid item xs={12} sm={3}>
        <Card><CardContent>
          <Typography variant="caption" color="text.secondary">SINGLE-SOURCE CRITICAL</Typography>
          <Typography variant="h3" color={map.single_source_critical_count > 0 ? 'error' : 'success'}>
            {map.single_source_critical_count}
          </Typography>
          <Typography variant="caption">no fallback today</Typography>
        </CardContent></Card>
      </Grid>
      <Grid item xs={12} sm={3}>
        <Card><CardContent>
          <Typography variant="caption" color="text.secondary">GEO-CONCENTRATION ALERTS</Typography>
          <Typography variant="h3" color={(geo?.alerts.length ?? 0) > 0 ? 'warning.main' : 'success.main'}>
            {geo?.alerts.length ?? 0}
          </Typography>
          <Typography variant="caption">countries &gt; 30% exposure</Typography>
        </CardContent></Card>
      </Grid>

      {/* Geo alerts */}
      {(geo?.alerts.length ?? 0) > 0 && (
        <Grid item xs={12}>
          {geo!.alerts.map((a, i) => (
            <Alert key={i} severity="warning" icon={<Warning />} sx={{ mb: 1 }}>
              <b>{a.country}</b>: {a.alert} — <i>{a.recommendation}</i>
            </Alert>
          ))}
        </Grid>
      )}

      {/* Node table */}
      <Grid item xs={12} md={7}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>Supplier Nodes</Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Tier</TableCell>
                    <TableCell>Supplier</TableCell>
                    <TableCell>Country</TableCell>
                    <TableCell>Criticality</TableCell>
                    <TableCell>Flags</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {map.nodes.sort((a, b) => a.tier - b.tier).map(n => (
                    <TableRow key={n.node_id}>
                      <TableCell>
                        <Chip size="small" label={`T${n.tier}`}
                          sx={{ bgcolor: tierColor(n.tier), color: 'white' }} />
                      </TableCell>
                      <TableCell>
                        <b>{n.name}</b>
                        {n.is_single_source && (
                          <Chip size="small" label="Single Source" color="error"
                            sx={{ ml: 1, height: 18 }} />
                        )}
                        <br />
                        <Typography variant="caption" color="text.secondary">
                          {n.products_or_services.join(', ')}
                        </Typography>
                      </TableCell>
                      <TableCell>{n.country}{n.city ? `, ${n.city}` : ''}</TableCell>
                      <TableCell>
                        <Chip size="small" label={n.criticality}
                          color={n.criticality === 'critical' ? 'error'
                                 : n.criticality === 'high' ? 'warning' : 'default'} />
                      </TableCell>
                      <TableCell>
                        {n.risk_flags.map(f => <Chip key={f} label={f} size="small" sx={{ mr: 0.5 }} />)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      </Grid>

      {/* Country concentration */}
      <Grid item xs={12} md={5}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>Country Concentration</Typography>
            {geo?.concentrations.map(c => (
              <Box key={c.country} sx={{ mb: 1.5 }}>
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body2">
                    {c.country} <small>({c.node_count} nodes)</small>
                  </Typography>
                  <Typography variant="body2">{c.spend_share_pct}% spend</Typography>
                </Stack>
                <LinearProgress variant="determinate" value={c.spend_share_pct}
                  color={c.spend_share_pct > 30 ? 'error' : 'primary'}
                  sx={{ height: 8, borderRadius: 4 }} />
              </Box>
            ))}

            <Divider sx={{ my: 2 }} />
            <Typography variant="h6" gutterBottom>Single-Source Risks</Typography>
            {singleSource?.risks?.length ? singleSource.risks.map((r: any) => (
              <Alert key={r.node_id} severity="error" sx={{ mb: 1 }}>
                <b>{r.name}</b> (T{r.tier}, {r.country})<br />
                <small>{r.recommendation}</small>
              </Alert>
            )) : <Typography variant="body2" color="success.main">No single-source dependencies detected ✓</Typography>}
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
};

// ============================================================
// Agent Tab — natural-language MESH assistant with tool-call trace
// ============================================================
const SUGGESTED_QUESTIONS = [
  'Where do I have the most geographic concentration risk and what should I do about it?',
  'Which suppliers are single points of failure for critical components?',
  'Run a geopolitical risk scan on US tariffs hitting our Chinese tier-2 suppliers in Q3 2026.',
  'Summarise my overall MESH posture and the top 3 actions for the next 90 days.',
  'How does the BSI MESH framework define the Harmonization pillar?',
];

const AgentTab: React.FC = () => {
  const [question, setQuestion] = useState('');
  const [companyName, setCompanyName] = useState('Acme Electronics');
  const [industry, setIndustry] = useState('Electronics');
  // Conversation state
  const [conversations, setConversations] = useState<ConversationSummary[]>([]);
  const [activeConvId, setActiveConvId] = useState<string | null>(null);
  // Each conversation message; assistant messages also keep the tool trace.
  type ChatMessage =
    | { role: 'user'; text: string }
    | {
        role: 'model'; text: string;
        tool_calls: AgentToolCall[]; iterations: number; ok: boolean;
        citations: Citation[]; streaming?: boolean;
      };
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Citation chunk viewer
  const [chunkOpen, setChunkOpen] = useState(false);
  const [chunkLoading, setChunkLoading] = useState(false);
  const [chunkData, setChunkData] = useState<ChunkDetail | null>(null);

  const loadConversations = async () => {
    try {
      const res = await conversationsApi.list();
      setConversations(res.conversations);
    } catch (e: any) {
      setError(e?.response?.data?.detail || e.message || 'Failed to load conversations');
    }
  };

  useEffect(() => { loadConversations(); }, []);

  const loadConversation = async (id: string) => {
    setError(null);
    try {
      const detail = await conversationsApi.get(id);
      setActiveConvId(id);
      // Convert persisted messages to our ChatMessage shape
      const msgs: ChatMessage[] = detail.messages.map(m =>
        m.role === 'user'
          ? { role: 'user', text: m.text }
          : {
              role: 'model', text: m.text,
              tool_calls: m.tool_calls || [],
              citations: m.citations || [],
              iterations: m.iterations, ok: m.ok,
            }
      );
      setMessages(msgs);
    } catch (e: any) {
      setError(e?.response?.data?.detail || e.message || 'Failed to load thread');
    }
  };

  const newConversation = () => {
    setActiveConvId(null);
    setMessages([]);
    setError(null);
    setQuestion('');
  };

  const renameConv = async (id: string) => {
    const t = window.prompt('New title:');
    if (!t || !t.trim()) return;
    try {
      await conversationsApi.rename(id, t.trim());
      await loadConversations();
    } catch (e: any) { setError(e?.message || 'Rename failed'); }
  };

  const deleteConv = async (id: string) => {
    if (!window.confirm('Delete this conversation?')) return;
    try {
      await conversationsApi.remove(id);
      if (id === activeConvId) newConversation();
      await loadConversations();
    } catch (e: any) { setError(e?.message || 'Delete failed'); }
  };

  const openChunk = async (chunkId: string) => {
    setChunkOpen(true); setChunkLoading(true); setChunkData(null);
    try {
      const data = await docsApi.getChunk(chunkId);
      setChunkData(data);
    } catch (e: any) {
      setChunkData({
        chunk_id: chunkId, doc_id: '', chunk_index: 0,
        text: e?.response?.data?.detail || e.message || 'Failed to load chunk',
        document: { title: 'Error', tags: [], source: '' },
      });
    } finally { setChunkLoading(false); }
  };

  /** Render answer text with [#1], [#2] markers as clickable chips. */
  const renderAnswer = (text: string, citations: Citation[]) => {
    const byMarker = new Map(citations.map(c => [c.marker, c]));
    const parts = text.split(/(\[#\d+\])/g);
    return (
      <>
        {parts.map((part, idx) => {
          const m = part.match(/^\[#(\d+)\]$/);
          if (m) {
            const cit = byMarker.get(`#${m[1]}`);
            if (cit) {
              return (
                <Tooltip key={idx} title={cit.title} arrow>
                  <Chip label={`#${m[1]}`} size="small" color="primary" variant="outlined"
                    onClick={() => openChunk(cit.chunk_id)}
                    sx={{ mx: 0.25, height: 20, cursor: 'pointer', verticalAlign: 'baseline' }} />
                </Tooltip>
              );
            }
          }
          return <span key={idx}>{part}</span>;
        })}
      </>
    );
  };

  const ask = async (q?: string) => {
    const finalQ = (q ?? question).trim();
    if (!finalQ || loading) return;
    setError(null);
    // Optimistic: append user + an empty streaming model message
    setMessages(prev => [
      ...prev,
      { role: 'user', text: finalQ },
      { role: 'model', text: '', tool_calls: [], iterations: 0,
        ok: false, citations: [], streaming: true },
    ]);
    setQuestion('');
    setLoading(true);

    const updateLast = (mut: (m: any) => void) => {
      setMessages(prev => {
        const next = [...prev];
        for (let i = next.length - 1; i >= 0; i--) {
          if (next[i].role === 'model' && (next[i] as any).streaming) {
            const m: any = { ...next[i] };
            mut(m);
            next[i] = m;
            break;
          }
        }
        return next;
      });
    };

    // Tell backend: existing conversation_id, OR "new" to auto-create
    const conversationIdParam = activeConvId || 'new';

    try {
      await meshApi.streamAgent(
        {
          question: finalQ,
          company_name: companyName,
          industry,
          conversation_id: conversationIdParam,
        },
        {
          onConversation: (cid) => {
            // Backend assigned (or echoed) the conversation id
            if (!activeConvId) setActiveConvId(cid);
          },
          onToolCall: (tool, args) => updateLast(m => {
            m.tool_calls = [...m.tool_calls, { tool, args, result: null }];
          }),
          onToolResult: (tool, result) => updateLast(m => {
            const tc = [...m.tool_calls];
            for (let i = tc.length - 1; i >= 0; i--) {
              if (tc[i].tool === tool && tc[i].result === null) {
                tc[i] = { ...tc[i], result }; break;
              }
            }
            m.tool_calls = tc;
          }),
          onText: (delta) => updateLast(m => { m.text = (m.text || '') + delta; }),
          onCitations: (cits) => updateLast(m => { m.citations = cits; }),
          onDone: (payload) => {
            updateLast(m => {
              m.iterations = payload.iterations;
              m.ok = payload.ok;
              m.streaming = false;
              if (payload.answer && !m.text) m.text = payload.answer;
            });
            // Refresh sidebar so message_count updates
            loadConversations();
          },
          onError: (msg) => {
            setError(msg);
            updateLast(m => { m.streaming = false; m.ok = false; });
          },
        },
      );
    } catch (e: any) {
      setError(e?.message || 'Stream failed');
      updateLast((m: any) => { m.streaming = false; m.ok = false; });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Grid container spacing={3}>
      <Grid item xs={12} md={4}>
        <Card>
          <CardContent>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
              <AutoAwesome color="primary" />
              <Typography variant="h6">MESH Agent</Typography>
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Multi-turn chat. The agent remembers context across questions and
              chains MESH tools (map, geo concentration, single-source risks,
              risk scans, framework explainers) to answer.
            </Typography>
            <Stack spacing={2}>
              <Stack direction="row" spacing={1}>
                <TextField label="Company" size="small" value={companyName}
                  onChange={e => setCompanyName(e.target.value)} fullWidth />
                <TextField label="Industry" size="small" value={industry}
                  onChange={e => setIndustry(e.target.value)} fullWidth />
              </Stack>
              <Divider />
              <Typography variant="caption" color="text.secondary">SUGGESTED</Typography>
              <Stack spacing={1}>
                {SUGGESTED_QUESTIONS.map(q => (
                  <Button key={q} size="small" variant="outlined"
                    sx={{ justifyContent: 'flex-start', textTransform: 'none' }}
                    onClick={() => ask(q)} disabled={loading}>
                    {q}
                  </Button>
                ))}
              </Stack>
              <Divider />
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="caption" color="text.secondary">
                  CONVERSATIONS ({conversations.length})
                </Typography>
                <Stack direction="row" spacing={0.5}>
                  <Tooltip title="Refresh"><IconButton size="small" onClick={loadConversations}><Refresh fontSize="small" /></IconButton></Tooltip>
                  <Tooltip title="New chat"><IconButton size="small" color="primary" onClick={newConversation}><Add fontSize="small" /></IconButton></Tooltip>
                </Stack>
              </Stack>
              <Box sx={{ maxHeight: 260, overflowY: 'auto', border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                {conversations.length === 0 && (
                  <Typography variant="caption" color="text.secondary" sx={{ p: 1.5, display: 'block' }}>
                    No conversations yet. Ask a question to start one.
                  </Typography>
                )}
                {conversations.map(c => (
                  <Box key={c.id}
                    onClick={() => loadConversation(c.id)}
                    sx={{
                      px: 1, py: 0.75, cursor: 'pointer',
                      borderBottom: '1px solid', borderColor: 'divider',
                      bgcolor: c.id === activeConvId ? 'action.selected' : 'transparent',
                      '&:hover': { bgcolor: 'action.hover' },
                      display: 'flex', alignItems: 'center', gap: 0.5,
                    }}>
                    <ChatBubble fontSize="small" color={c.id === activeConvId ? 'primary' : 'disabled'} />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" noWrap title={c.title}>{c.title}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {c.message_count} msgs · {new Date(c.updated_at).toLocaleDateString()}
                      </Typography>
                    </Box>
                    <IconButton size="small" onClick={(e) => { e.stopPropagation(); renameConv(c.id); }}>
                      <Edit fontSize="inherit" />
                    </IconButton>
                    <IconButton size="small" onClick={(e) => { e.stopPropagation(); deleteConv(c.id); }}>
                      <Typography variant="caption">×</Typography>
                    </IconButton>
                  </Box>
                ))}
              </Box>
              <Typography variant="caption" color="text.secondary">
                {activeConvId ? `Active: ${activeConvId.slice(0, 8)}…` : 'New chat (unsaved)'}
              </Typography>
            </Stack>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12} md={8}>
        <Card>
          <CardContent>
            <Box sx={{ maxHeight: 600, overflowY: 'auto', mb: 2,
              display: 'flex', flexDirection: 'column', gap: 2 }}>
              {messages.length === 0 && (
                <Alert severity="info" icon={<AutoAwesome />}>
                  Start a conversation. Ask a follow-up like "and what about
                  Vietnam?" — the agent remembers prior turns.
                </Alert>
              )}
              {messages.map((m, idx) => (
                m.role === 'user' ? (
                  <Box key={idx} sx={{ alignSelf: 'flex-end', maxWidth: '85%' }}>
                    <Paper sx={{ p: 1.5, bgcolor: 'primary.main', color: 'primary.contrastText',
                      borderRadius: 2 }}>
                      <Typography variant="body2">{m.text}</Typography>
                    </Paper>
                  </Box>
                ) : (
                  <Box key={idx} sx={{ alignSelf: 'flex-start', maxWidth: '95%', width: '95%' }}>
                    <Paper sx={{ p: 2, bgcolor: 'grey.50',
                      borderLeft: 4, borderColor: 'primary.main', borderRadius: 2 }}>
                      <Stack direction="row" spacing={1} sx={{ mb: 1 }} alignItems="center">
                        <Chip size="small" label="Agent" color="primary" />
                        <Chip size="small" variant="outlined"
                          label={`${m.tool_calls.length} tools`} />
                        {!m.streaming && (
                          <Chip size="small" variant="outlined"
                            label={`${m.iterations} iters`} />
                        )}
                        {m.streaming && (
                          <Stack direction="row" spacing={0.5} alignItems="center">
                            <CircularProgress size={12} />
                            <Typography variant="caption" color="text.secondary">streaming…</Typography>
                          </Stack>
                        )}
                        {m.citations.length > 0 && (
                          <Chip size="small" color="success" variant="outlined"
                            label={`${m.citations.length} sources`} />
                        )}
                        {!m.ok && !m.streaming && <Chip size="small" color="warning" label="incomplete" />}
                      </Stack>
                      <Typography component="div" sx={{
                        whiteSpace: 'pre-wrap', fontSize: 14, lineHeight: 1.6,
                      }}>
                        {renderAnswer(m.text || (m.streaming ? '…' : ''), m.citations)}
                      </Typography>

                      {m.citations.length > 0 && (
                        <Box sx={{ mt: 1.5 }}>
                          <Accordion disableGutters defaultExpanded
                            sx={{ bgcolor: 'transparent', boxShadow: 'none' }}>
                            <AccordionSummary expandIcon={<ExpandMore />}
                              sx={{ p: 0, minHeight: 0 }}>
                              <Typography variant="caption" color="text.secondary">
                                <Description fontSize="inherit" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
                                Sources ({m.citations.length})
                              </Typography>
                            </AccordionSummary>
                            <AccordionDetails sx={{ p: 0, pt: 1 }}>
                              <Stack spacing={0.75}>
                                {m.citations.map(c => (
                                  <Paper key={c.chunk_id} variant="outlined"
                                    sx={{ p: 1, cursor: 'pointer',
                                      '&:hover': { bgcolor: 'action.hover' } }}
                                    onClick={() => openChunk(c.chunk_id)}>
                                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.25 }}>
                                      <Chip size="small" label={c.marker} color="primary" />
                                      <Typography variant="caption" fontWeight={600}>{c.title}</Typography>
                                      <Chip size="small" variant="outlined"
                                        label={`score ${c.score?.toFixed?.(2) ?? c.score}`} />
                                    </Stack>
                                    <Typography variant="caption" color="text.secondary">
                                      {c.preview}
                                    </Typography>
                                  </Paper>
                                ))}
                              </Stack>
                            </AccordionDetails>
                          </Accordion>
                        </Box>
                      )}

                      {m.tool_calls.length > 0 && (
                        <Box sx={{ mt: 1 }}>
                          <Accordion disableGutters
                            sx={{ bgcolor: 'transparent', boxShadow: 'none' }}>
                            <AccordionSummary expandIcon={<ExpandMore />}
                              sx={{ p: 0, minHeight: 0 }}>
                              <Typography variant="caption" color="text.secondary">
                                <Build fontSize="inherit" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
                                Tool trace ({m.tool_calls.length})
                              </Typography>
                            </AccordionSummary>
                            <AccordionDetails sx={{ p: 0 }}>
                              {m.tool_calls.map((tc, i) => (
                                <Accordion key={i} disableGutters>
                                  <AccordionSummary expandIcon={<ExpandMore />}>
                                    <Stack direction="row" spacing={1} alignItems="center">
                                      <Chip label={`#${i + 1}`} size="small" />
                                      <Typography variant="body2" fontWeight={600}>
                                        {tc.tool}
                                      </Typography>
                                    </Stack>
                                  </AccordionSummary>
                                  <AccordionDetails>
                                    {Object.keys(tc.args).length > 0 && (
                                      <Paper variant="outlined" sx={{ p: 1, mb: 1, bgcolor: 'grey.100' }}>
                                        <pre style={{ margin: 0, fontSize: 11 }}>
                                          {JSON.stringify(tc.args, null, 2)}
                                        </pre>
                                      </Paper>
                                    )}
                                    <Paper variant="outlined" sx={{ p: 1, bgcolor: 'grey.100',
                                      maxHeight: 300, overflow: 'auto' }}>
                                      <pre style={{ margin: 0, fontSize: 10 }}>
                                        {JSON.stringify(tc.result, null, 2)}
                                      </pre>
                                    </Paper>
                                  </AccordionDetails>
                                </Accordion>
                              ))}
                            </AccordionDetails>
                          </Accordion>
                        </Box>
                      )}
                    </Paper>
                  </Box>
                )
              ))}
              {loading && (
                <Box sx={{ alignSelf: 'flex-start' }}>
                  <Paper sx={{ p: 1.5, bgcolor: 'grey.100' }}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <CircularProgress size={16} />
                      <Typography variant="caption">Agent is thinking…</Typography>
                    </Stack>
                  </Paper>
                </Box>
              )}
            </Box>
            {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}
            <Stack direction="row" spacing={1}>
              <TextField
                fullWidth size="small"
                placeholder={messages.length === 0
                  ? "Ask anything about your supply chain..."
                  : "Follow up..."}
                value={question}
                onChange={e => setQuestion(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    ask();
                  }
                }}
                disabled={loading}
                multiline maxRows={4}
              />
              <Button variant="contained" onClick={() => ask()}
                disabled={loading || !question.trim()}>
                {loading ? <CircularProgress size={20} /> : 'Send'}
              </Button>
            </Stack>
          </CardContent>
        </Card>
      </Grid>

      <Dialog open={chunkOpen} onClose={() => setChunkOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {chunkLoading ? 'Loading source…' : (chunkData?.document?.title || 'Source')}
          {chunkData && (
            <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
              {chunkData.document.tags.map(t => <Chip key={t} size="small" label={t} />)}
              <Chip size="small" variant="outlined"
                label={`chunk #${chunkData.chunk_index + 1}`} />
            </Stack>
          )}
        </DialogTitle>
        <DialogContent dividers>
          {chunkLoading ? (
            <Stack direction="row" spacing={1} alignItems="center">
              <CircularProgress size={18} /> <Typography variant="body2">Fetching chunk…</Typography>
            </Stack>
          ) : (
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace', fontSize: 13 }}>
              {chunkData?.text}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setChunkOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Grid>
  );
};

// ============================================================
// Documents Tab — RAG document store (ingest, list, search, delete)
// ============================================================
const DocumentsTab: React.FC = () => {
  const [docs, setDocs] = useState<RAGDoc[]>([]);
  const [stats, setStats] = useState<DocsStats | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [title, setTitle] = useState('');
  const [tags, setTags] = useState('');
  const [text, setText] = useState('');
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [urlInput, setUrlInput] = useState('');
  const [urlStatus, setUrlStatus] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [searchResults, setSearchResults] = useState<RAGSearchResult[]>([]);
  const [searching, setSearching] = useState(false);

  const loadDocs = async () => {
    try {
      const res = await docsApi.list();
      setDocs(res.documents);
      setStats(res.stats);
    } catch (e: any) {
      setError(e?.response?.data?.detail || e.message || 'Load failed');
    }
  };

  useEffect(() => { loadDocs(); }, []);

  const handleIngest = async () => {
    if (!title.trim() || !text.trim()) return;
    setLoading(true); setError(null);
    try {
      await docsApi.ingest({
        title: title.trim(), text: text.trim(),
        tags: tags.split(',').map(t => t.trim()).filter(Boolean),
      });
      setTitle(''); setTags(''); setText('');
      await loadDocs();
    } catch (e: any) {
      setError(e?.response?.data?.detail || e.message || 'Ingest failed');
    } finally { setLoading(false); }
  };

  const handleUpload = async () => {
    if (!uploadFile) return;
    setLoading(true); setError(null);
    try {
      await docsApi.upload(uploadFile, undefined, tags);
      setUploadFile(null);
      await loadDocs();
    } catch (e: any) {
      setError(e?.response?.data?.detail || e.message || 'Upload failed');
    } finally { setLoading(false); }
  };

  const handleIngestUrl = async () => {
    const u = urlInput.trim();
    if (!u) return;
    setLoading(true); setError(null); setUrlStatus(null);
    try {
      const tagList = tags.split(',').map(t => t.trim()).filter(Boolean);
      const res = await docsApi.ingestUrl(u, tagList);
      setUrlStatus(
        `✅ Ingested "${res.fetched.title}" (${res.fetched.char_count.toLocaleString()} chars · ${res.document.chunk_count} chunks)`,
      );
      setUrlInput('');
      await loadDocs();
    } catch (e: any) {
      setError(e?.response?.data?.detail || e.message || 'URL ingest failed');
    } finally { setLoading(false); }
  };

  const handleDelete = async (doc_id: string) => {
    if (!window.confirm('Delete this document and all its chunks?')) return;
    try {
      await docsApi.remove(doc_id);
      await loadDocs();
    } catch (e: any) {
      setError(e?.response?.data?.detail || e.message || 'Delete failed');
    }
  };

  const handleSearch = async () => {
    if (!query.trim()) return;
    setSearching(true); setError(null);
    try {
      const res = await docsApi.search(query.trim(), 5);
      setSearchResults(res.results);
    } catch (e: any) {
      setError(e?.response?.data?.detail || e.message || 'Search failed');
    } finally { setSearching(false); }
  };

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Alert severity="info" icon={<Description />}>
          Ingest contracts, incident logs, supplier reports, or policies. The
          MESH Agent automatically searches these via the <b>search_documents</b>
          {' '}tool when answering questions.
          {stats && (
            <Box component="span" sx={{ ml: 2 }}>
              <Chip size="small" label={`${stats.documents} docs`} sx={{ mr: 1 }} />
              <Chip size="small" label={`${stats.chunks} chunks`} sx={{ mr: 1 }} />
              <Chip size="small" label={`${(stats.total_chars / 1000).toFixed(1)}k chars`} />
            </Box>
          )}
        </Alert>
      </Grid>

      {error && (
        <Grid item xs={12}>
          <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>
        </Grid>
      )}

      <Grid item xs={12} md={6}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              <CloudUpload sx={{ verticalAlign: 'middle', mr: 1 }} />
              Add a Document
            </Typography>
            <Stack spacing={2}>
              <TextField label="Title" value={title} size="small"
                onChange={e => setTitle(e.target.value)} fullWidth />
              <TextField label="Tags (comma-separated)" value={tags} size="small"
                placeholder="contract, tier-1, uk"
                onChange={e => setTags(e.target.value)} fullWidth />
              <TextField label="Content" value={text} multiline rows={6}
                placeholder="Paste contract text, incident notes, policy excerpts…"
                onChange={e => setText(e.target.value)} fullWidth />
              <Button variant="contained" onClick={handleIngest}
                disabled={loading || !title.trim() || !text.trim()}>
                {loading ? <CircularProgress size={22} /> : 'Ingest Text'}
              </Button>

              <Divider>OR UPLOAD FILE</Divider>
              <Button variant="outlined" component="label" startIcon={<CloudUpload />}>
                {uploadFile ? uploadFile.name : 'Choose .txt / .md / .pdf / .docx'}
                <input type="file" hidden
                  accept=".txt,.md,.markdown,.pdf,.docx,text/plain,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                  onChange={e => setUploadFile(e.target.files?.[0] || null)} />
              </Button>
              <Button variant="contained" onClick={handleUpload}
                disabled={loading || !uploadFile}>
                Upload & Ingest
              </Button>

              <Divider>OR FETCH URL</Divider>
              <TextField label="Web URL" value={urlInput} size="small"
                placeholder="https://example.com/article"
                onChange={e => setUrlInput(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleIngestUrl()}
                fullWidth />
              <Button variant="contained" color="secondary" onClick={handleIngestUrl}
                disabled={loading || !urlInput.trim()} startIcon={<CloudUpload />}>
                {loading ? <CircularProgress size={22} /> : 'Fetch & Ingest URL'}
              </Button>
              {urlStatus && (
                <Alert severity="success" onClose={() => setUrlStatus(null)}
                  sx={{ fontSize: 12 }}>
                  {urlStatus}
                </Alert>
              )}
            </Stack>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12} md={6}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              <Search sx={{ verticalAlign: 'middle', mr: 1 }} />
              Test Semantic Search
            </Typography>
            <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
              <TextField fullWidth size="small" placeholder="e.g. force majeure tariff clauses"
                value={query} onChange={e => setQuery(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleSearch()} />
              <Button variant="contained" onClick={handleSearch}
                disabled={searching || !query.trim()}>
                {searching ? <CircularProgress size={20} /> : 'Search'}
              </Button>
            </Stack>
            <Box sx={{ maxHeight: 460, overflowY: 'auto' }}>
              {searchResults.length === 0 && !searching && (
                <Typography variant="caption" color="text.secondary">
                  No results yet. Ingest some docs and run a search.
                </Typography>
              )}
              {searchResults.map((r, i) => (
                <Paper key={r.chunk_id} variant="outlined" sx={{ p: 1.5, mb: 1 }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
                    <Typography variant="caption" fontWeight={600}>
                      #{i + 1} · {r.title}
                    </Typography>
                    <Chip size="small" label={`score ${r.score.toFixed(3)}`}
                      color={r.score > 0.7 ? 'success' : r.score > 0.5 ? 'warning' : 'default'} />
                  </Stack>
                  <Typography variant="body2" sx={{ fontSize: 13 }}>
                    {r.text.slice(0, 400)}{r.text.length > 400 ? '…' : ''}
                  </Typography>
                </Paper>
              ))}
            </Box>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Indexed Documents ({docs.length})
            </Typography>
            {docs.length === 0 ? (
              <Alert severity="info">No documents yet. Add one above.</Alert>
            ) : (
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Title</TableCell>
                      <TableCell>Tags</TableCell>
                      <TableCell align="right">Chars</TableCell>
                      <TableCell align="right">Chunks</TableCell>
                      <TableCell>Source</TableCell>
                      <TableCell>Created</TableCell>
                      <TableCell align="right" />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {docs.map(d => (
                      <TableRow key={d.doc_id}>
                        <TableCell>
                          <Typography variant="body2" fontWeight={600}>{d.title}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {d.doc_id.slice(0, 8)}…
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Stack direction="row" gap={0.5} flexWrap="wrap">
                            {d.tags.map(t => <Chip key={t} label={t} size="small" />)}
                          </Stack>
                        </TableCell>
                        <TableCell align="right">{d.char_count.toLocaleString()}</TableCell>
                        <TableCell align="right">
                          <Chip size="small" label={d.chunk_count}
                            color={d.chunk_count === 0 ? 'error' : 'default'} />
                        </TableCell>
                        <TableCell>{d.source}</TableCell>
                        <TableCell>
                          <Typography variant="caption">
                            {new Date(d.created_at).toLocaleString()}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <IconButton size="small" color="error"
                            onClick={() => handleDelete(d.doc_id)}>
                            <Delete fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
};

// ============================================================================
// WatchtowerTab - daily proactive risk briefings
// ============================================================================
const RISK_COLOR: Record<string, 'error' | 'warning' | 'info' | 'success' | 'default'> = {
  CRITICAL: 'error', HIGH: 'warning', MEDIUM: 'info', LOW: 'success', UNKNOWN: 'default',
};

const WatchtowerTab: React.FC = () => {
  const [latest, setLatest] = useState<Briefing | null>(null);
  const [history, setHistory] = useState<BriefingSummary[]>([]);
  const [scheduler, setScheduler] = useState<SchedulerStatus | null>(null);
  const [loading, setLoading] = useState(false);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [companyName, setCompanyName] = useState('Acme Electronics');
  const [industry, setIndustry] = useState('Electronics');
  const [hour, setHour] = useState('7');
  const [minute, setMinute] = useState('0');

  const reload = async () => {
    setLoading(true);
    try {
      const [lat, lst, sch] = await Promise.all([
        watchtowerApi.latest().catch(() => null),
        watchtowerApi.list(20),
        watchtowerApi.schedulerStatus(),
      ]);
      setLatest(lat);
      setHistory(lst.briefings);
      setScheduler(sch);
    } catch (e: any) {
      setError(e?.response?.data?.detail || e.message || 'Failed to load');
    } finally { setLoading(false); }
  };

  useEffect(() => { reload(); }, []);

  const runNow = async () => {
    setRunning(true); setError(null);
    try {
      const b = await watchtowerApi.run(companyName, industry);
      setLatest(b);
      await reload();
    } catch (e: any) {
      setError(e?.response?.data?.detail || e.message || 'Briefing failed');
    } finally { setRunning(false); }
  };

  const startSched = async () => {
    try {
      const r = await watchtowerApi.schedulerStart(
        parseInt(hour, 10), parseInt(minute, 10), companyName, industry
      );
      setScheduler({ running: true, next_run: r?.next_run || null });
      await reload();
    } catch (e: any) { setError(e?.message || 'Scheduler start failed'); }
  };

  const stopSched = async () => {
    try { await watchtowerApi.schedulerStop(); await reload(); }
    catch (e: any) { setError(e?.message || 'Scheduler stop failed'); }
  };

  const loadHistorical = async (id: string) => {
    setLoading(true);
    try { setLatest(await watchtowerApi.get(id)); }
    catch (e: any) { setError(e?.message || 'Load failed'); }
    finally { setLoading(false); }
  };

  const risk = (latest?.risk_level || 'UNKNOWN').toUpperCase();

  return (
    <Grid container spacing={3}>
      {/* Hero / latest briefing */}
      <Grid item xs={12} md={8}>
        <Card>
          <CardContent>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
              <Visibility color="primary" />
              <Typography variant="h6">Daily Risk Briefing</Typography>
              {latest && (
                <Chip label={risk} color={RISK_COLOR[risk] || 'default'} size="small" sx={{ fontWeight: 700 }} />
              )}
              <Box sx={{ flex: 1 }} />
              {latest && (
                <Button
                  variant="outlined"
                  href={watchtowerApi.pdfUrl(latest.id)}
                  target="_blank"
                  rel="noopener"
                  startIcon={<Description />}
                >
                  PDF
                </Button>
              )}
              <Button startIcon={<PlayArrow />} variant="contained" color="primary"
                onClick={runNow} disabled={running}>
                {running ? 'Running… (~45s)' : 'Run briefing now'}
              </Button>
            </Stack>
            {running && <LinearProgress sx={{ mb: 2 }} />}
            {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
            {!latest && !loading && (
              <Alert severity="info">
                No briefings yet. Click <b>Run briefing now</b> to generate today's
                proactive risk assessment, or start the scheduler for daily auto-runs.
              </Alert>
            )}
            {latest && (
              <Stack spacing={2}>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Generated {new Date(latest.created_at).toLocaleString()} · {latest.duration_ms ? `${(latest.duration_ms / 1000).toFixed(1)}s` : ''}
                  </Typography>
                </Box>
                {latest.summary && (
                  <Paper variant="outlined" sx={{ p: 2, bgcolor: 'background.default' }}>
                    <Typography variant="subtitle2" gutterBottom>Summary</Typography>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{latest.summary}</Typography>
                  </Paper>
                )}
                {latest.key_findings && latest.key_findings.length > 0 && (
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>Key Findings ({latest.key_findings.length})</Typography>
                    <List dense>
                      {latest.key_findings.map((f, i) => (
                        <ListItem key={i} sx={{ alignItems: 'flex-start' }}>
                          <ListItemText primary={`• ${f}`} primaryTypographyProps={{ variant: 'body2' }} />
                        </ListItem>
                      ))}
                    </List>
                  </Paper>
                )}
                {latest.recommendations && latest.recommendations.length > 0 && (
                  <Paper variant="outlined" sx={{ p: 2, borderLeft: '4px solid', borderLeftColor: 'primary.main' }}>
                    <Typography variant="subtitle2" gutterBottom>Recommended Actions ({latest.recommendations.length})</Typography>
                    <List dense>
                      {latest.recommendations.map((r, i) => (
                        <ListItem key={i} sx={{ alignItems: 'flex-start' }}>
                          <ListItemText primary={`${i + 1}. ${r}`} primaryTypographyProps={{ variant: 'body2' }} />
                        </ListItem>
                      ))}
                    </List>
                  </Paper>
                )}
                {latest.tool_calls && latest.tool_calls.length > 0 && (
                  <Accordion>
                    <AccordionSummary expandIcon={<ExpandMore />}>
                      <Typography variant="caption">
                        Agent trace · {latest.tool_calls.length} tool call(s)
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Stack spacing={0.5}>
                        {latest.tool_calls.map((t, i) => (
                          <Typography key={i} variant="caption" sx={{ fontFamily: 'monospace' }}>
                            {i + 1}. {t.tool}({Object.keys(t.args || {}).join(', ')})
                          </Typography>
                        ))}
                      </Stack>
                    </AccordionDetails>
                  </Accordion>
                )}
                {latest.citations && latest.citations.length > 0 && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Citations: {latest.citations.length}
                    </Typography>
                  </Box>
                )}
                {latest.full_answer && (
                  <Accordion>
                    <AccordionSummary expandIcon={<ExpandMore />}>
                      <Typography variant="caption">Full briefing text</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace', fontSize: 12 }}>
                        {latest.full_answer}
                      </Typography>
                    </AccordionDetails>
                  </Accordion>
                )}
              </Stack>
            )}
          </CardContent>
        </Card>
      </Grid>

      {/* Right column: scheduler + history */}
      <Grid item xs={12} md={4}>
        <Stack spacing={3}>
          <Card>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                <Schedule color="primary" />
                <Typography variant="h6">Scheduler</Typography>
                <Box sx={{ flex: 1 }} />
                <Chip
                  label={scheduler?.running ? 'RUNNING' : 'STOPPED'}
                  color={scheduler?.running ? 'success' : 'default'}
                  size="small"
                />
              </Stack>
              <Stack spacing={1.5}>
                <TextField label="Company" size="small" value={companyName}
                  onChange={e => setCompanyName(e.target.value)} fullWidth />
                <TextField label="Industry" size="small" value={industry}
                  onChange={e => setIndustry(e.target.value)} fullWidth />
                <Stack direction="row" spacing={1}>
                  <TextField label="Hour (UTC)" size="small" value={hour} type="number"
                    onChange={e => setHour(e.target.value)} fullWidth
                    inputProps={{ min: 0, max: 23 }} />
                  <TextField label="Minute" size="small" value={minute} type="number"
                    onChange={e => setMinute(e.target.value)} fullWidth
                    inputProps={{ min: 0, max: 59 }} />
                </Stack>
                {scheduler?.next_run && (
                  <Alert severity="info" icon={<Schedule />}>
                    Next run: {new Date(scheduler.next_run).toLocaleString()}
                  </Alert>
                )}
                <Stack direction="row" spacing={1}>
                  <Button size="small" variant="contained" onClick={startSched} fullWidth>
                    {scheduler?.running ? 'Reschedule' : 'Start'}
                  </Button>
                  <Button size="small" color="warning" onClick={stopSched}
                    disabled={!scheduler?.running} fullWidth>
                    Stop
                  </Button>
                </Stack>
              </Stack>
            </CardContent>
          </Card>

          <Card>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                <Typography variant="h6" sx={{ flex: 1 }}>History ({history.length})</Typography>
                <IconButton size="small" onClick={reload}><Refresh fontSize="small" /></IconButton>
              </Stack>
              {history.length === 0 ? (
                <Typography variant="caption" color="text.secondary">
                  No past briefings.
                </Typography>
              ) : (
                <List dense sx={{ maxHeight: 360, overflowY: 'auto' }}>
                  {history.map(b => {
                    const r = (b.risk_level || 'UNKNOWN').toUpperCase();
                    return (
                      <ListItem key={b.id} button onClick={() => loadHistorical(b.id)}
                        sx={{ borderBottom: '1px solid', borderColor: 'divider' }}>
                        <ListItemText
                          primary={
                            <Stack direction="row" spacing={1} alignItems="center">
                              <Chip label={r} color={RISK_COLOR[r] || 'default'} size="small"
                                sx={{ fontWeight: 700, minWidth: 70 }} />
                              <Typography variant="body2" noWrap>{b.title}</Typography>
                            </Stack>
                          }
                          secondary={`${new Date(b.created_at).toLocaleString()} · ${b.status}`}
                          secondaryTypographyProps={{ variant: 'caption' }}
                        />
                      </ListItem>
                    );
                  })}
                </List>
              )}
            </CardContent>
          </Card>
        </Stack>
      </Grid>
    </Grid>
  );
};

export default MESHDashboard;
